import React, { useState, useEffect } from 'react';
import { Discount, DiscountStatus } from '../../types/discount';
import { discountService } from '../../services/discountService';
import MetricCard from '../Common/MetricCard';

interface PromotionMonitorProps {
  discounts: Discount[];
  onRefresh: () => void;
}

interface PromotionAlert {
  id: string;
  type: 'expiring' | 'budget_low' | 'usage_high' | 'expired';
  discount: Discount;
  message: string;
  severity: 'low' | 'medium' | 'high';
}

const PromotionMonitor: React.FC<PromotionMonitorProps> = ({
  discounts,
  onRefresh
}) => {
  const [alerts, setAlerts] = useState<PromotionAlert[]>([]);
  const [activePromotions, setActivePromotions] = useState<Discount[]>([]);
  const [summary, setSummary] = useState({
    totalActive: 0,
    expiringSoon: 0,
    budgetAlerts: 0,
    usageAlerts: 0
  });

  useEffect(() => {
    analyzePromotions();
  }, [discounts]);

  const analyzePromotions = () => {
    const active = discounts.filter(d => d.status === DiscountStatus.ACTIVE);
    setActivePromotions(active);

    const newAlerts: PromotionAlert[] = [];
    let expiringSoonCount = 0;
    let budgetAlertsCount = 0;
    let usageAlertsCount = 0;

    active.forEach(discount => {
      // Check for expiring discounts
      if (discountService.isDiscountExpiringSoon(discount, 7)) {
        expiringSoonCount++;
        const daysLeft = Math.ceil((new Date(discount.validUntil).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
        newAlerts.push({
          id: `expiring-${discount._id}`,
          type: 'expiring',
          discount,
          message: `Expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`,
          severity: daysLeft <= 3 ? 'high' : daysLeft <= 7 ? 'medium' : 'low'
        });
      }

      // Check for budget alerts
      if (discount.budgetLimit > 0) {
        const budgetPercentage = discountService.calculateBudgetPercentage(discount);
        if (budgetPercentage >= 90) {
          budgetAlertsCount++;
          newAlerts.push({
            id: `budget-${discount._id}`,
            type: 'budget_low',
            discount,
            message: `${budgetPercentage}% of budget used`,
            severity: budgetPercentage >= 95 ? 'high' : 'medium'
          });
        }
      }

      // Check for usage alerts
      if (discount.usageLimit > 0) {
        const usagePercentage = discountService.calculateUsagePercentage(discount);
        if (usagePercentage >= 90) {
          usageAlertsCount++;
          newAlerts.push({
            id: `usage-${discount._id}`,
            type: 'usage_high',
            discount,
            message: `${usagePercentage}% of usage limit reached`,
            severity: usagePercentage >= 95 ? 'high' : 'medium'
          });
        }
      }
    });

    // Check for expired discounts that are still active (shouldn't happen but good to monitor)
    discounts.forEach(discount => {
      if (discount.status === DiscountStatus.ACTIVE && new Date(discount.validUntil) < new Date()) {
        newAlerts.push({
          id: `expired-${discount._id}`,
          type: 'expired',
          discount,
          message: 'Discount has expired but is still active',
          severity: 'high'
        });
      }
    });

    setAlerts(newAlerts);
    setSummary({
      totalActive: active.length,
      expiringSoon: expiringSoonCount,
      budgetAlerts: budgetAlertsCount,
      usageAlerts: usageAlertsCount
    });
  };

  const getAlertIcon = (type: PromotionAlert['type']) => {
    switch (type) {
      case 'expiring':
        return '⏰';
      case 'budget_low':
        return '💰';
      case 'usage_high':
        return '📊';
      case 'expired':
        return '❌';
      default:
        return '⚠️';
    }
  };

  const getAlertColor = (severity: PromotionAlert['severity']) => {
    switch (severity) {
      case 'high':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'low':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTopPerformingPromotions = () => {
    return activePromotions
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 5);
  };

  const getTotalDiscountGiven = () => {
    return activePromotions.reduce((total, discount) => total + discount.budgetUsed, 0);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Active Promotions"
          value={summary.totalActive.toString()}
          change={summary.totalActive > 0 ? { value: summary.totalActive, type: 'increase' } : undefined}
        />
        <MetricCard
          title="Expiring Soon"
          value={summary.expiringSoon.toString()}
          change={summary.expiringSoon > 0 ? { value: summary.expiringSoon, type: 'decrease' } : undefined}
        />
        <MetricCard
          title="Budget Alerts"
          value={summary.budgetAlerts.toString()}
          change={summary.budgetAlerts > 0 ? { value: summary.budgetAlerts, type: 'decrease' } : undefined}
        />
        <MetricCard
          title="Usage Alerts"
          value={summary.usageAlerts.toString()}
          change={summary.usageAlerts > 0 ? { value: summary.usageAlerts, type: 'decrease' } : undefined}
        />
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Promotion Alerts</h3>
            <button
              onClick={onRefresh}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              Refresh
            </button>
          </div>
          <div className="space-y-3">
            {alerts.map(alert => (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border ${getAlertColor(alert.severity)}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <span className="text-lg">{getAlertIcon(alert.type)}</span>
                    <div>
                      <div className="font-medium">
                        {alert.discount.name} ({alert.discount.code})
                      </div>
                      <div className="text-sm mt-1">{alert.message}</div>
                      <div className="text-xs mt-2 opacity-75">
                        {alert.type === 'expiring' && `Valid until: ${new Date(alert.discount.validUntil).toLocaleDateString()}`}
                        {alert.type === 'budget_low' && `Budget: ${formatCurrency(alert.discount.budgetUsed)} / ${formatCurrency(alert.discount.budgetLimit)}`}
                        {alert.type === 'usage_high' && `Usage: ${alert.discount.usageCount} / ${alert.discount.usageLimit}`}
                      </div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    alert.severity === 'high' ? 'bg-red-100 text-red-800' :
                    alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {alert.severity.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Promotions */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Top Performing Promotions</h3>
          {getTopPerformingPromotions().length > 0 ? (
            <div className="space-y-3">
              {getTopPerformingPromotions().map((discount, index) => (
                <div key={discount._id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-800">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{discount.name}</div>
                      <div className="text-sm text-gray-500">{discount.code}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-900">{discount.usageCount} uses</div>
                    <div className="text-sm text-gray-500">
                      {formatCurrency(discount.budgetUsed)} given
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              No active promotions found
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Stats</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Discount Given</span>
              <span className="font-medium text-gray-900">
                {formatCurrency(getTotalDiscountGiven())}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Usage per Promotion</span>
              <span className="font-medium text-gray-900">
                {activePromotions.length > 0 
                  ? Math.round(activePromotions.reduce((sum, d) => sum + d.usageCount, 0) / activePromotions.length)
                  : 0
                }
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Most Popular Type</span>
              <span className="font-medium text-gray-900 capitalize">
                {(() => {
                  if (activePromotions.length === 0) return 'None';
                  const counts = activePromotions.reduce((acc, d) => {
                    acc[d.type] = (acc[d.type] || 0) + 1;
                    return acc;
                  }, {} as Record<string, number>);
                  const entries = Object.entries(counts).sort(([,a], [,b]) => b - a);
                  return entries[0]?.[0]?.replace('_', ' ') || 'None';
                })()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Auto-Apply Promotions</span>
              <span className="font-medium text-gray-900">
                {activePromotions.filter(d => d.isAutoApply).length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* No Alerts Message */}
      {alerts.length === 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="text-green-600 text-lg mb-2">✅</div>
          <div className="text-green-800 font-medium">All promotions are running smoothly</div>
          <div className="text-green-600 text-sm mt-1">No alerts or issues detected</div>
        </div>
      )}
    </div>
  );
};

export default PromotionMonitor;